import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  ThemeToggle, Button
} from '../components/ui';

export default function Login() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { loginWithGoogle, loginWithEmail, currentUser, error: authError } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle();
      navigate('/');
    } catch (err) {
      // Only surface plain thrown errors (e.g. "Access denied..."). Firebase
      // SDK errors carry a `code` and are already mapped to friendly text in
      // AuthContext via setError → exposed as authError.
      if (err.message && !err.code) setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError('');
    setLoading(true);
    try {
      await loginWithEmail(email.trim(), password);
      navigate('/');
    } catch (err) {
      // Only surface plain thrown errors (e.g. "Access denied..."). Firebase
      // SDK errors carry a `code` and are already mapped to friendly text in
      // AuthContext via setError → exposed as authError.
      if (err.message && !err.code) setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-hover dark:bg-background flex items-center justify-center px-4 transition-colors duration-200">
      <div className="w-full max-w-md space-y-8">
        {/* Logo/Header */}
        <div className="text-center">
          <img
            src={theme === 'dark' ? "/logo.png" : "/logolight.png"}
            alt="SeyAI Insights Logo"
            className="mx-auto h-16 w-auto mb-4"
          />
        </div>

        {/* Theme Toggle */}
        <ThemeToggle className="absolute -top-4 right-4" />

        {/* Login Card */}
        <div className="bg-surface dark:bg-background-gray rounded-lg p-8 border border-border-light dark:border-border">
          <div className="space-y-6">
            {/* Email/Password Form */}
            <form onSubmit={handleEmailSignIn} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Email address"
                autoComplete="email"
                className="w-full rounded-lg border border-border-light dark:border-border bg-background-light dark:bg-background px-3 py-2.5 text-sm text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                className="w-full rounded-lg border border-border-light dark:border-border bg-background-light dark:bg-background px-3 py-2.5 text-sm text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="w-full"
                disabled={loading || !email.trim() || !password}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <p className="text-xs text-center text-text-muted-light dark:text-text-muted">
              Don't have an account?{' '}
              <Link to="/signup" className="text-brand-primary hover:underline">Create one</Link>
            </p>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border-light dark:bg-border" />
              <span className="text-xs text-text-muted-light dark:text-text-muted">or</span>
              <div className="flex-1 h-px bg-border-light dark:bg-border" />
            </div>

            {/* Google Sign-In Button */}
            <Button
              variant="secondary"
              size="md"
              className="w-full flex space-x-3 items-center justify-center"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>
                {loading ? 'Signing in...' : 'Sign in with Google'}
              </span>
            </Button>

            {/* Error Message */}
            {(error || authError) && (
              <div className="bg-red-50 dark:bg-danger-light border border-red-300 dark:border-danger rounded-lg p-4">
                <p className="text-red-600 dark:text-danger text-sm text-center">{error || authError}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
